/**
 * YardDropInstrumentationPanel — iter386c / D1
 *
 * Renders the last 50 drop-attempt entries captured by
 * `utils/yardDropInstrumentation.js` with a copy-to-clipboard
 * control.  Read-only.  No console output.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Copy, Trash2, RefreshCw, Check, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  getDropAttempts,
  serialiseDropAttempts,
  clearDropAttempts,
} from '../utils/yardDropInstrumentation';
import {
  getSaveCycles,
  serialiseSaveCycles,
  clearSaveCycles,
} from '../utils/yardSaveTimerInstrumentation';

export function YardDropInstrumentationPanel() {
  return (
    <>
      <YardDropAttemptsCard />
      <YardSaveCyclesCard />
    </>
  );
}

function YardDropAttemptsCard() {
  const [entries, setEntries] = useState([]);
  const [expandedIdx, setExpandedIdx] = useState(null);

  const refresh = useCallback(() => {
    setEntries(getDropAttempts().slice().reverse()); // newest first
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(serialiseDropAttempts());
      toast.success(`Copied ${entries.length} drop attempt(s) to clipboard`);
    } catch (_e) {
      toast.error('Copy failed — check clipboard permissions');
    }
  };

  const copyOne = async (entry) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(entry, null, 2));
      toast.success('Copied entry to clipboard');
    } catch (_e) {
      toast.error('Copy failed');
    }
  };

  const clear = () => {
    clearDropAttempts();
    setEntries([]);
    toast.success('Drop-attempt buffer cleared');
  };

  return (
    <Card className="mb-4 border-blue-200" data-testid="yard-drop-instrumentation-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              Yard drop instrumentation (D1)
              <Badge variant="secondary" className="text-xs">{entries.length}/50</Badge>
            </CardTitle>
            <CardDescription>
              Read-only observations of every drop attempt on the Yard canvas.
              Captures pointer coords, canvas transform, every candidate container
              considered, and the resolver&apos;s chosen outcome.
              Does <b>not</b> alter drop behaviour.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={refresh} data-testid="drop-instr-refresh">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={copyAll} data-testid="drop-instr-copy-all">
              <Copy className="w-4 h-4 mr-1" /> Copy all
            </Button>
            <Button size="sm" variant="outline" onClick={clear} data-testid="drop-instr-clear">
              <Trash2 className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            No drop attempts recorded yet.  Perform a drag & drop on the Yard, then Refresh.
          </div>
        ) : (
          <div className="divide-y max-h-[500px] overflow-y-auto">
            {entries.map((entry, i) => {
              const chosen = entry.chosen || {};
              const registered = chosen.kind === 'stack' || chosen.kind === 'block';
              return (
                <div
                  key={`${entry.timestamp}-${i}`}
                  className={`py-2 text-sm cursor-pointer hover:bg-muted/40 px-2 rounded ${entry.suspicious ? 'bg-red-50 border-l-4 border-red-500' : ''}`}
                  onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                  data-testid={`drop-instr-row-${i}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {entry.suspicious
                        ? <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                        : registered
                          ? <Check className="w-4 h-4 text-green-600 shrink-0" />
                          : <X className="w-4 h-4 text-amber-600 shrink-0" />}
                      <span className="font-mono text-xs text-muted-foreground shrink-0">
                        {new Date(entry.timestamp).toLocaleTimeString([], { hour12: false })}
                      </span>
                      <span className="font-mono text-xs truncate">{entry.draggedId}</span>
                      <Badge variant="outline" className="text-[10px]">{entry.draggedType || '?'}</Badge>
                      <span className="text-xs text-muted-foreground">→</span>
                      <Badge
                        variant="secondary"
                        className={`text-[10px] ${
                          chosen.kind === 'stack' ? 'bg-blue-100 text-blue-700' :
                          chosen.kind === 'block' ? 'bg-purple-100 text-purple-700' :
                                                    'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {chosen.kind}{chosen.id ? ` · ${chosen.id.slice(-8)}` : ''}
                      </Badge>
                      {entry.suspicious && (
                        <Badge variant="destructive" className="text-[10px]" data-testid={`drop-instr-suspicious-${i}`}>
                          SUSPICIOUS
                        </Badge>
                      )}
                      {entry.itemDragMoved === false && (
                        <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">click-only</Badge>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); copyOne(entry); }}
                      className="h-6 px-2 text-[10px]"
                      data-testid={`drop-instr-copy-${i}`}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  {expandedIdx === i && (
                    <pre className="mt-2 p-2 bg-muted/50 rounded text-[11px] font-mono overflow-x-auto whitespace-pre-wrap break-all">
                      {JSON.stringify(entry, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default YardDropInstrumentationPanel;

/**
 * Save-cycles diagnostic — D1b-1.  One entry per autosave cycle:
 * first dirty → every reset → PUT dispatch → ack.  Shows the
 * total elapsed ms so we can compare against the configured 2000 ms
 * debounce and see whether reset behaviour is silently widening it.
 */
function YardSaveCyclesCard() {
  const [cycles, setCycles] = useState([]);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const refresh = useCallback(() => {
    setCycles(getSaveCycles().slice().reverse());
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(serialiseSaveCycles());
      toast.success(`Copied ${cycles.length} save cycle(s)`);
    } catch (_e) { toast.error('Copy failed'); }
  };
  const clear = () => {
    clearSaveCycles();
    setCycles([]);
    toast.success('Save-cycle buffer cleared');
  };
  return (
    <Card className="mb-4 border-emerald-200" data-testid="yard-save-cycles-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              Yard autosave cycles (D1b-1)
              <Badge variant="secondary" className="text-xs">{cycles.length}/30</Badge>
            </CardTitle>
            <CardDescription>
              Per-cycle timeline: first dirty → every timer reset (with trigger) →
              PUT dispatch → ack.  Configured debounce is 2000 ms; use these
              numbers to see if resets are silently widening it.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={refresh} data-testid="save-cycles-refresh">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={copyAll} data-testid="save-cycles-copy-all">
              <Copy className="w-4 h-4 mr-1" /> Copy all
            </Button>
            <Button size="sm" variant="outline" onClick={clear} data-testid="save-cycles-clear">
              <Trash2 className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {cycles.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            No save cycles recorded yet.  Perform an edit on the Yard, wait for
            the PUT to fire, then Refresh.
          </div>
        ) : (
          <div className="divide-y max-h-[400px] overflow-y-auto">
            {cycles.map((cyc, i) => {
              const total = cyc.totalMs ?? '—';
              const overDebounce = typeof cyc.totalMs === 'number' && cyc.totalMs > 2500;
              return (
                <div
                  key={`${cyc.firstDirtyAt}-${i}`}
                  className={`py-2 text-sm cursor-pointer hover:bg-muted/40 px-2 rounded ${overDebounce ? 'bg-amber-50 border-l-4 border-amber-500' : ''}`}
                  onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                  data-testid={`save-cycle-row-${i}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground shrink-0">
                      {new Date(cyc.firstDirtyAt).toLocaleTimeString([], { hour12: false })}
                    </span>
                    <Badge variant="outline" className="text-[10px]">{cyc.firstDirtyTrigger}</Badge>
                    <span className="text-xs text-muted-foreground">·</span>
                    <span className="text-xs">resets: <b>{cyc.resetCount}</b></span>
                    <span className="text-xs text-muted-foreground">·</span>
                    <span className={`text-xs ${overDebounce ? 'text-amber-700 font-semibold' : ''}`}>
                      total: <b>{total} ms</b>
                    </span>
                    {cyc.errorAt && (
                      <Badge variant="destructive" className="text-[10px] ml-2">ERROR</Badge>
                    )}
                  </div>
                  {expandedIdx === i && (
                    <pre className="mt-2 p-2 bg-muted/50 rounded text-[11px] font-mono overflow-x-auto whitespace-pre-wrap break-all">
                      {JSON.stringify(cyc, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
